import React, { useEffect, useState, useCallback, useMemo } from 'react';
import client from '../api/client';
import './HierarchyPicker.css';

/**
 * Slick hierarchy picker that lets a user:
 *  - select a parent node (all descendants included by default)
 *  - explicitly exclude individual children
 *  - combine disjoint branches into a single group
 *
 * Value shape:
 * {
 *   includedNodeIds: string[],
 *   excludedNodeIds: string[],
 *   includeChildrenByDefault: boolean
 * }
 *
 * When a parent is fully checked, only the parent id goes into includedNodeIds.
 * When a parent is indeterminate, the parent id is in includedNodeIds and any unchecked
 * descendants are in excludedNodeIds.
 */
function HierarchyPicker({ value, onChange, showResolvedFacilities = true }) {
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('all');

  const includedSet = useMemo(() => new Set(value?.includedNodeIds || []), [value]);
  const excludedSet = useMemo(() => new Set(value?.excludedNodeIds || []), [value]);
  const includeChildrenByDefault = value?.includeChildrenByDefault !== false;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await client.get('/api/customers/hierarchy');
        if (!cancelled) setHierarchy(res.data?.hierarchy || []);
      } catch (err) {
        if (!cancelled) setError('Failed to load hierarchy');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const applyChange = useCallback((nextIncluded, nextExcluded) => {
    onChange?.({
      includedNodeIds: Array.from(nextIncluded),
      excludedNodeIds: Array.from(nextExcluded),
      includeChildrenByDefault
    });
  }, [onChange, includeChildrenByDefault]);

  const isNodeEffectivelyIncluded = useCallback((node, ancestorIncluded) => {
    if (excludedSet.has(node.id)) return false;
    if (includedSet.has(node.id)) return true;
    return ancestorIncluded;
  }, [includedSet, excludedSet]);

  // Returns the effective checked state for a node given its ancestors' inclusion state:
  // 'checked' (all descendants selected), 'indeterminate' (some), 'unchecked' (none).
  const getCheckState = useCallback((node, ancestorIncluded = false) => {
    const countDescendantLeaves = (n, ancIncluded) => {
      const nodeIncluded = isNodeEffectivelyIncluded(n, ancIncluded);
      const inherited = includeChildrenByDefault ? nodeIncluded : false;
      if (!n.children || n.children.length === 0) {
        return { total: 1, included: nodeIncluded ? 1 : 0 };
      }
      let total = 0;
      let included = 0;
      for (const c of n.children) {
        const r = countDescendantLeaves(c, inherited);
        total += r.total;
        included += r.included;
      }
      return { total, included };
    };

    const { total, included } = countDescendantLeaves(node, ancestorIncluded);
    if (included === 0) return 'unchecked';
    if (included === total) return 'checked';
    return 'indeterminate';
  }, [isNodeEffectivelyIncluded, includeChildrenByDefault]);

  const collectAllDescendants = (node) => {
    const ids = [node.id];
    if (node.children) node.children.forEach(c => ids.push(...collectAllDescendants(c)));
    return ids;
  };

  const handleToggle = useCallback((node, ancestorIncluded) => {
    const state = getCheckState(node, ancestorIncluded);
    const nextIncluded = new Set(includedSet);
    const nextExcluded = new Set(excludedSet);
    const descendants = collectAllDescendants(node);

    if (state === 'checked' || state === 'indeterminate') {
      descendants.forEach(id => { nextIncluded.delete(id); nextExcluded.delete(id); });
      if (ancestorIncluded && includeChildrenByDefault) {
        nextExcluded.add(node.id);
      }
    } else {
      descendants.forEach(id => nextExcluded.delete(id));
      nextIncluded.add(node.id);
    }

    applyChange(nextIncluded, nextExcluded);
  }, [getCheckState, includedSet, excludedSet, applyChange, includeChildrenByDefault]);

  const toggleExpand = useCallback((id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const nodeSelfMatches = useCallback((node) => {
    if (!search) return true;
    const s = search.toLowerCase();
    if (node.label?.toLowerCase().includes(s)) return true;
    if (node.billingCity?.toLowerCase().includes(s)) return true;
    if (node.billingState?.toLowerCase().includes(s)) return true;
    return false;
  }, [search]);

  const matchesSearch = useCallback((node) => {
    if (!search) return true;
    if (nodeSelfMatches(node)) return true;
    if (node.children?.some(matchesSearch)) return true;
    return false;
  }, [search, nodeSelfMatches]);

  const subtreeHasIncluded = useCallback((node, ancestorIncluded) => {
    if (isNodeEffectivelyIncluded(node, ancestorIncluded)) return true;
    if (!node.children || node.children.length === 0) return false;
    return node.children.some(c => subtreeHasIncluded(c, false));
  }, [isNodeEffectivelyIncluded]);

  const totalIncludedLeaves = useMemo(() => {
    const walk = (node, ancestorIncluded) => {
      const selfIncluded = isNodeEffectivelyIncluded(node, ancestorIncluded);
      const childAncestor = includeChildrenByDefault ? selfIncluded : false;
      if (!node.children || node.children.length === 0) {
        return selfIncluded ? 1 : 0;
      }
      let sum = 0;
      for (const c of node.children) sum += walk(c, childAncestor);
      return sum;
    };
    return hierarchy.reduce((acc, n) => acc + walk(n, false), 0);
  }, [hierarchy, isNodeEffectivelyIncluded, includeChildrenByDefault]);

  const autoExpandForSearch = useMemo(() => {
    if (!search) return new Set();
    const set = new Set();
    const walk = (node, ancestors) => {
      if (nodeSelfMatches(node)) {
        ancestors.forEach(a => set.add(a));
        set.add(node.id);
      }
      if (node.children) {
        node.children.forEach(c => walk(c, [...ancestors, node.id]));
      }
    };
    hierarchy.forEach(n => walk(n, []));
    return set;
  }, [hierarchy, search, nodeSelfMatches]);

  const autoExpandForSelected = useMemo(() => {
    if (viewMode !== 'selected') return new Set();
    const set = new Set();
    const walk = (node, ancestors, ancestorIncluded) => {
      const selfIncluded = isNodeEffectivelyIncluded(node, ancestorIncluded);
      const childAncestor = includeChildrenByDefault ? selfIncluded : false;
      if (subtreeHasIncluded(node, ancestorIncluded)) {
        ancestors.forEach(a => set.add(a));
        set.add(node.id);
      }
      if (node.children) {
        node.children.forEach(c => walk(c, [...ancestors, node.id], childAncestor));
      }
    };
    hierarchy.forEach(n => walk(n, [], false));
    return set;
  }, [hierarchy, viewMode, isNodeEffectivelyIncluded, includeChildrenByDefault, subtreeHasIncluded]);

  const isExpanded = (id) =>
    expanded.has(id) || autoExpandForSearch.has(id) || autoExpandForSelected.has(id);

  const renderNode = (node, level = 0, ancestorIncluded = false, ancestorMatched = false) => {
    if (viewMode === 'selected' && !subtreeHasIncluded(node, ancestorIncluded)) return null;

    const passesSearch = !search || ancestorMatched || matchesSearch(node);
    if (!passesSearch) return null;

    const hasChildren = node.children?.length > 0;
    const state = getCheckState(node, ancestorIncluded);
    const selfIncluded = isNodeEffectivelyIncluded(node, ancestorIncluded);
    const childAncestorIncluded = includeChildrenByDefault ? selfIncluded : false;
    const childAncestorMatched = ancestorMatched || nodeSelfMatches(node);

    return (
      <div key={node.id} className="hp-node" style={{ paddingLeft: `${level * 1.25}rem` }}>
        <div className="hp-node-row">
          {hasChildren ? (
            <button type="button" className="hp-expand" onClick={() => toggleExpand(node.id)}>
              {isExpanded(node.id) ? '▼' : '▶'}
            </button>
          ) : <span className="hp-spacer" />}

          <label className="hp-label">
            <input
              type="checkbox"
              checked={state === 'checked'}
              ref={el => { if (el) el.indeterminate = state === 'indeterminate'; }}
              onChange={() => handleToggle(node, ancestorIncluded)}
            />
            <span className={`hp-name ${hasChildren ? 'hp-parent' : ''}`}>{node.label}</span>
            {node.nodeType && <span className="hp-type">{node.nodeType}</span>}
            {hasChildren && <span className="hp-count">{node.children.length}</span>}
          </label>
          {(node.billingCity || node.billingState) && (
            <span className="hp-location">
              {node.billingCity}{node.billingCity && node.billingState ? ', ' : ''}{node.billingState}
            </span>
          )}
        </div>
        {hasChildren && isExpanded(node.id) && node.children.map(c =>
          renderNode(c, level + 1, childAncestorIncluded, childAncestorMatched)
        )}
      </div>
    );
  };

  if (loading) return <div className="hp-loading">Loading hierarchy…</div>;
  if (error) return <div className="hp-error">{error}</div>;
  if (hierarchy.length === 0) return <div className="hp-empty">No facilities loaded. Refresh facilities first.</div>;

  const emptySelected = viewMode === 'selected' && totalIncludedLeaves === 0;

  return (
    <div className="hp-root">
      <div className="hp-toolbar">
        <input
          type="text"
          className="hp-search"
          placeholder={viewMode === 'selected' ? 'Filter selected…' : 'Search facilities…'}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="hp-view-toggle" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'all'}
            className={viewMode === 'all' ? 'active' : ''}
            onClick={() => setViewMode('all')}
          >
            All
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'selected'}
            className={viewMode === 'selected' ? 'active' : ''}
            onClick={() => setViewMode('selected')}
            title="Show only facilities that are effectively included"
          >
            Selected <span className="hp-view-toggle-count">{totalIncludedLeaves}</span>
          </button>
        </div>
        <button
          type="button"
          className="hp-summary"
          onClick={() => setViewMode(viewMode === 'selected' ? 'all' : 'selected')}
          title="Toggle selected-only view"
        >
          {totalIncludedLeaves} facilit{totalIncludedLeaves === 1 ? 'y' : 'ies'} included
          {value?.excludedNodeIds?.length ? ` · ${value.excludedNodeIds.length} excluded` : ''}
        </button>
      </div>
      <div className="hp-tree">
        {emptySelected ? (
          <div className="hp-empty-selected">
            No facilities selected yet. Switch to <button type="button" className="hp-inline-link" onClick={() => setViewMode('all')}>All</button> to pick some.
          </div>
        ) : (
          hierarchy.map(n => renderNode(n))
        )}
      </div>
    </div>
  );
}

export default HierarchyPicker;
